import { useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Divider, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { LocaleContext } from "../../../../routing/LangRouter";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

//import redux store
import { saveSelectedTicket, saveTicketSelectedFilters } from "../../../../store/actions";

//import constants
import { ItineraryContext } from "../../../../constants/layouts/itinerary-layout";
import { BREAKPOINT, IS_MOBILE, WIDTH } from "../../../../constants/breakpoints";

//import utils
import { numberWithCommas } from "../../../../utils/utility";
import { flightDataMapper } from "../../../../utils/flightDataMapper";

//import general components
import BaseTabs from "../../../common/base-tabs/base-tabs";

//import view components
import FareRulesModal from "../international/int-flight-fare";
import BaggageRulesModal from "../international/int-flight-baggage";
import { FlightItemDetails } from "./flight-item-details";
import AvailabiltyModal from "../../availability/availabilty-modal";

//import images and icons
import { Airplane, ArrowUp2, Notification } from "iconsax-react";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";

//import styles
import "./flight-item.scss";

function FlightItem({ dispatch, flightItem, isFull }) {
    //general variables
    const navigate = useNavigate();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [_, setSearchParams] = useSearchParams();

    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));

    const isTwoWay = domesticFlightInformation?.returnDate;
    const normalizedFlight = flightDataMapper(isTwoWay, flightItem);

    // Render a price row for a specific passenger type
    const renderPriceRow = (passengerType, count, price) => {
        if (count <= 0) return null;

        return (
            <Grid container item xs={12} justifyContent="space-between">
                <span className="flight-item__price-details">
                    {count} {t(passengerType)}:
                </span>
                <span className="flight-item__price-details">
                    {flightItem?.tag === "zm" && passengerType !== "adult"
                        ? "قبل از پرداخت مشخص می شود"
                        : `${numberWithCommas(price * count)} ${t("currency")}`}
                </span>
            </Grid>
        );
    };

    // Render the total price row
    const { adult } = domesticFlightInformation.personCounter;
    const { adult_price, tag } = flightItem ?? {};

    const totalPrice = tag === "zm" ? adult_price * adult : normalizedFlight?.totalPrice;
    const renderTotalPrice = () => {
        return (
            <Grid container item xs={12} justifyContent="space-between">
                <span className="flight-item__price-details">{t("total")}:</span>
                <span className="flight-item__price-details">
                    {numberWithCommas(totalPrice)} {t("currency")}
                </span>
            </Grid>
        );
    };

    //check flight capacity
    const checkFlightCapacity = capacity => {
        const totalPersons = domesticFlightInformation.personCounter.adult + domesticFlightInformation.personCounter.child;
        if (capacity < totalPersons) {
            toast.error(t("flightMessage1"), { toastId: "custom-id-yes" });
            return false;
        } else return true;
    };

    //change the value to regenerate the itinerary
    const updateItineraryGenerate = () => {
        if (isItinerary) {
            itineraryInformation.itineraryGenerate = true;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }
    };

    //update the itinerary date and time based on the selected flight
    const updateItineraryDateTime = (type, date, time, hours) => {
        const fullDate = moment(`${date} ${time.split(":", 1).join(":00")}`)
            .add(hours, "hours")
            .format()
            .split("+")[0];
        const [newDate, newTime] = fullDate.split("T");
        if (type === "departure") {
            itineraryInformation.departureDate = newDate;
            itineraryInformation.firstDayTime = newTime;
        } else if (type === "return") {
            itineraryInformation.returnDate = newDate;
            itineraryInformation.lastDayTime = newTime;
        }
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    //navigate to the next page
    const navigateNextPage = () => {
        const isAccommodation = generalInformation?.isAccommodation;
        setRoutes([...routes, `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`]));
        navigate(`/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`);
    };

    //handle filter cancellation
    const resetFilters = () => {
        dispatch?.(saveTicketSelectedFilters(null));
    };

    //handle selected ticket
    const handleSelectedTicket = async ticket => {
        const normalizedDepartureTicket = flightDataMapper(isTwoWay, ticket, ticket?.segments);
        const normalizedReturnTicket = flightDataMapper(isTwoWay, ticket, ticket?.return_segments);

        if (ticket?.tag !== "pr") {
            if (!checkFlightCapacity(ticket?.capacity)) return;
        }
        updateItineraryGenerate();
        resetFilters();

        sessionStorage.setItem("selectedFlight", JSON.stringify(ticket));
        if (isItinerary) {
            updateItineraryDateTime("departure", normalizedDepartureTicket?.departureDate, normalizedDepartureTicket?.departureTime, 5);
            if (!!isTwoWay) {
                updateItineraryDateTime("return", normalizedReturnTicket?.departureDate, normalizedReturnTicket?.departureTime, -3);
            }
            navigateNextPage();
        } else navigate(`/${locale}/flight-passengers`);
    };

    //mobile functions
    const handleFlightDetails = (action, flightItem) => {
        if (WIDTH < BREAKPOINT) {
            const params = { action };
            setSearchParams(params);
            sessionStorage.setItem("flightDetails", JSON.stringify(flightItem));
        }
    };

    //list of tabs
    const [activeTab, setActiveTab] = useState({ refundRules: false, baggageRules: false });
    const tabList = [
        { type: "refundRules", title: t("refundRules"), activeTab: activeTab?.refundRules, to: `/${locale}/refund-rules` },
        { type: "baggageRules", title: t("baggageRules"), activeTab: activeTab?.baggageRules },
    ];

    const handleTabChange = (type, to) => {
        if (type === "refundRules" && flightItem?.tag !== "pr") {
            window.open(to, "_blank");
        } else {
            setActiveTab({ [type]: true });
        }
    };

    // States for managing baggage information and fare rules
    const [baggageInfo, setBaggageInfo] = useState(null);
    const [fareRules, setFareRules] = useState([]);

    const handleReserve = () => {
        dispatch(saveSelectedTicket(flightItem));
        setIsReserveModalOpen(true);
    };

    return (
        <Grid
            className={`flight-item ${isFull && "full-flight"}`}
            container
            item
            xs={12}
            justifyContent="center"
            onClick={!isFull ? () => handleFlightDetails("flight-details", flightItem) : null}
        >
            <Grid className="flight-item__details" container item xs={12} md={9.5} rowGap={{ xs: 1.5, md: 3 }} justifyContent="center">
                <FlightItemDetails flightItem={flightItem} details={flightItem?.segments} title={t("departureFlight")} />
                {!!isTwoWay && (
                    <>
                        <Divider width="100%" />
                        <FlightItemDetails flightItem={flightItem} details={flightItem?.return_segments} title={t("returnFlight")} />
                    </>
                )}
                {!isFull && (
                    <Grid container item xs={12} columnGap={2} display={{ xs: "none", md: "flex" }}>
                        <BaseTabs handleTabChange={handleTabChange} list={tabList} style={{ color: "var(--primary-color)" }} />
                    </Grid>
                )}

                <Grid
                    className={`flight-item__rules ${activeTab.baggageRules || activeTab.refundRules ? "open" : "close"}`}
                    container
                    item
                    xs={12}
                    alignItems="flex-start"
                >
                    {activeTab.baggageRules && (
                        <BaggageRulesModal
                            flightItem={flightItem}
                            baggageInfo={baggageInfo}
                            setBaggageInfo={setBaggageInfo}
                            fareSourceCode={flightItem?.fare_source_code}
                            uniqueId=""
                        />
                    )}
                    {activeTab.refundRules && (
                        <FareRulesModal fareRules={fareRules} setFareRules={setFareRules} fareSourceCode={flightItem?.fare_source_code} uniqueId="" />
                    )}
                </Grid>
                {(activeTab.baggageRules || activeTab.refundRules) && (
                    <span className="flight-item__close" onClick={() => setActiveTab({ baggageRules: false, refundRules: false })}>
                        {t("close")}
                        <ArrowUp2 size="20" color="var(--primary-color)" />
                    </span>
                )}
            </Grid>

            <Divider sx={{ width: { xs: "95%", md: "0" } }} width="100%" />

            <Grid className="flight-item__action" container item xs={12} md={2.5} justifyContent="center" alignItems="center">
                {normalizedFlight?.adultPrice !== 0 && (
                    <Grid container item xs={12} md={12} columnGap={1} justifyContent={{ xs: "space-between", md: "center" }} alignItems="center">
                        <span className="flight-item__price-title">{`${t("price")} ${t("perEach")} ${t("adult")}:`}</span>
                        <span>
                            <span className="flight-item__price">{numberWithCommas(normalizedFlight?.adultPrice)}</span>
                            {t("currency")}
                        </span>
                    </Grid>
                )}

                {!isTwoWay &&
                    flightItem?.capacity !== null &&
                    (flightItem?.capacity === 0 && !IS_MOBILE ? (
                        <Grid container item xs={12} justifyContent="space-evenly">
                            <BaseButton
                                text={<Notification />}
                                color="outline"
                                radius="radius-50"
                                size="circular"
                                handleClick={() => setIsNotificationModalOpen(true)}
                            />
                            <BaseButton text={<Airplane />} color="outline" radius="radius-50" size="circular" handleClick={handleReserve} />
                        </Grid>
                    ) : (
                        <Grid container item xs={12} xl={10} display={{ xs: "none", md: "flex" }}>
                            <BaseButton
                                text={t("select")}
                                color="primary"
                                size="medium"
                                radius="radius-10"
                                disabled={isFull}
                                handleClick={() => handleSelectedTicket(flightItem)}
                            />
                        </Grid>
                    ))}
                {!isTwoWay && flightItem?.capacity !== null && (
                    <Grid className="flight-item__capacity" container item xs={12} md={12} justifyContent={{ xs: "flex-start", md: "center" }}>
                        <span>{flightItem?.capacity === 0 ? t("fullCapacity") : `${t("capacity")}: `}</span>
                        <span>
                            {flightItem?.capacity} {locale === "fa" ? "نفر" : ""}
                        </span>
                    </Grid>
                )}

                {(activeTab.baggageRules || activeTab.refundRules) && (
                    <Grid className="flight-item__prices" container item xs={12} rowGap={1} justifyContent="center" alignItems="center">
                        {renderPriceRow("adult", domesticFlightInformation.personCounter.adult, normalizedFlight?.adultPrice)}
                        {renderPriceRow("child", domesticFlightInformation.personCounter.child, normalizedFlight?.childPrice)}
                        {renderPriceRow("infant", domesticFlightInformation.personCounter.infant, normalizedFlight?.infantPrice)}
                        <Divider width="100%" />
                        {renderTotalPrice()}
                    </Grid>
                )}
            </Grid>
            <AvailabiltyModal
                flightItem={flightItem}
                isModalOpen={isNotificationModalOpen}
                setIsModalOpen={setIsNotificationModalOpen}
                title={t("General.Notify When Available")}
                modalType="notification"
                pageUrl={`/${locale}/flight-availability`}
            />
            <AvailabiltyModal
                flightItem={flightItem}
                isModalOpen={isReserveModalOpen}
                setIsModalOpen={setIsReserveModalOpen}
                title={t("General.Reserve When Available")}
                modalType="reserve"
                pageUrl={`/${locale}/flight-automatic-reserve`}
            />
        </Grid>
    );
}

export default connect()(FlightItem);

// Revalidate flight

// const [revalidateLoading, setRevalidateLoading] = useState(false);
// const { revalidateBooking } = useRevalidate(generalInformation, setRevalidateLoading);

// const handleSelectedTicket = async ticket => {
// let validationValue;
// if (ticket.tag === "pr") {
//     await revalidateBooking(ticket?.fare_source_code, "domestic-flight").then(newPrice => {
//         validationValue = newPrice === ticket?.total_price ? true : false;
//     });
// }

//Checks the zamzam flight selling type compatibility between departure and return flights.
// const checkSellingType = (departureSelling, returnSelling) => {
//     if (!returnSelling) return true;
//     if (departureSelling === "All" || returnSelling === "All") return true;
//     if (departureSelling === returnSelling) return true;
//     const incompatibleSellingTypes = {
//         "Only Iranian": {
//             "Non Iranian": {
//                 en: "Your selected flight is only for non-Iranian passengers.",
//                 fa: "پرواز انتخابی شما فقط برای مسافران غیرایرانی است.",
//             },
//         },
//         "Non Iranian": {
//             "Only Iranian": {
//                 en: "Your selected flight is only for Iranian passengers.",
//                 fa: "پرواز انتخابی شما فقط برای مسافران ایرانی است.",
//             },
//         },
//     };
//     if (incompatibleSellingTypes[departureSelling] && incompatibleSellingTypes[departureSelling][returnSelling]) {
//         const errorMessage =
//             locale === "en"
//                 ? incompatibleSellingTypes[departureSelling][returnSelling].en
//                 : incompatibleSellingTypes[departureSelling][returnSelling].fa;
//         toast.error(errorMessage, { toastId: "custom-id-yes" });
//         return false;
//     }
//     return true;
// };
